import { useEffect, useRef, useState, type FormEvent } from "react";
import { AlertTriangle, ImagePlus } from "lucide-react";
import {
  Button, Card, EmptyState, ErrorText, FormSection, Input,
  PageHeader, Skeleton, useToast,
} from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";

interface BrandingForm {
  appNameAr: string;
  appNameEn: string;
  primaryColor: string;
  secondaryColor: string;
  loginHeadlineAr: string;
  loginHeadlineEn: string;
  footerTextAr: string;
  footerTextEn: string;
}

interface BrandingResponse extends BrandingForm {
  logoFileId: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
}

/**
 * Owner branding: identity, accent colour, logo.
 *
 * The accent colour submitted here is the RAW input — `buildAccentRamp` (see
 * BrandingContext) derives the full eight-step scale from it at load time,
 * pinning tonal structure and enforcing WCAG contrast. This screen does not
 * duplicate that logic; it only collects the one seed value.
 */
export function BrandingPage() {
  const { hasPermission } = useAuth();
  const { language, refresh } = useBranding();
  const { showToast } = useToast();
  const ar = language === "ar";

  const [form, setForm] = useState<BrandingForm | null>(null);
  const [current, setCurrent] = useState<BrandingResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const canEdit = hasPermission("branding.update");

  const load = async () => {
    setLoadError(null);
    try {
      const r = await apiFetch<BrandingResponse>("/settings/branding");
      setCurrent(r);
      setForm({
        appNameAr: r.appNameAr, appNameEn: r.appNameEn,
        primaryColor: r.primaryColor, secondaryColor: r.secondaryColor,
        loginHeadlineAr: r.loginHeadlineAr ?? "", loginHeadlineEn: r.loginHeadlineEn ?? "",
        footerTextAr: r.footerTextAr ?? "", footerTextEn: r.footerTextEn ?? "",
      });
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : ar ? "تعذّر تحميل الهوية." : "Couldn't load branding.");
    }
  };

  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaveError(null);
    setSaving(true);
    try {
      await apiFetch("/settings/branding", { method: "PUT", body: form });
      showToast("success", ar ? "حُفظت الهوية." : "Branding saved.");
      await refresh(); // the app-wide accent/logo/app-name context picks this up live
      await load();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : ar ? "تعذّر الحفظ." : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof BrandingForm>(key: K, value: BrandingForm[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const uploadLogo = async (file: File) => {
    setUploading(true);
    setSaveError(null);
    try {
      const fd = new FormData();
      fd.append("purpose", "logo");
      fd.append("file", file);
      await apiFetch("/settings/branding/asset", { method: "POST", formData: fd });
      showToast("success", ar ? "حُمِّل الشعار." : "Logo uploaded.");
      await refresh();
      await load();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : ar ? "تعذّر تحميل الشعار." : "Couldn't upload the logo.");
    } finally {
      setUploading(false);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <PageHeader title={ar ? "الهوية" : "Branding"} />
        <Card>
          <EmptyState icon={<AlertTriangle className="size-6" />} title={ar ? "تعذّر التحميل" : "Couldn't load"} description={loadError}
            action={<Button size="sm" onClick={load}>{ar ? "إعادة المحاولة" : "Retry"}</Button>} />
        </Card>
      </div>
    );
  }

  if (!form || !current) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-40" />
        <Card><Skeleton className="h-96 w-full" /></Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title={ar ? "الهوية" : "Branding"} subtitle={ar ? "الاسم والشعار واللون." : "Name, logo and accent colour."} />

      <Card>
        <FormSection legend={ar ? "الشعار" : "Logo"}>
          <div className="flex items-center gap-4">
            <div className="flex size-16 items-center justify-center overflow-hidden rounded-lg border border-line bg-ink-50">
              {current.logoUrl ? (
                <img src={current.logoUrl} alt="" className="max-h-full max-w-full object-contain" />
              ) : (
                <ImagePlus className="size-6 text-muted-subtle" />
              )}
            </div>
            {canEdit && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadLogo(f); e.target.value = ""; }}
                />
                <Button type="button" variant="outline" loading={uploading} onClick={() => fileRef.current?.click()}>
                  {ar ? "تغيير الشعار" : "Change logo"}
                </Button>
              </>
            )}
          </div>
        </FormSection>
      </Card>

      <form onSubmit={save} className="space-y-4">
        <Card>
          <FormSection legend={ar ? "الاسم" : "Name"}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label={ar ? "اسم التطبيق (عربي)" : "App name (Arabic)"} value={form.appNameAr} onChange={(e) => set("appNameAr", e.target.value)} disabled={!canEdit} required />
              <Input label={ar ? "اسم التطبيق (إنجليزي)" : "App name (English)"} value={form.appNameEn} onChange={(e) => set("appNameEn", e.target.value)} disabled={!canEdit} required />
            </div>
          </FormSection>
        </Card>

        <Card>
          <FormSection legend={ar ? "اللون" : "Colour"}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ink-700">{ar ? "اللون الأساسي" : "Accent colour"}</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.primaryColor} onChange={(e) => set("primaryColor", e.target.value)} disabled={!canEdit}
                    className="h-11 w-14 cursor-pointer rounded-lg border border-line-strong disabled:cursor-not-allowed" />
                  <Input value={form.primaryColor} onChange={(e) => set("primaryColor", e.target.value)} disabled={!canEdit} className="flex-1" />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {ar ? "تُشتق منه درجات فاتحة وداكنة تلقائيًا مع الحفاظ على وضوح النص." : "Lighter and darker steps are derived automatically, keeping text legible."}
                </p>
              </div>
              <Input label={ar ? "اللون الثانوي" : "Secondary colour"} value={form.secondaryColor} onChange={(e) => set("secondaryColor", e.target.value)} disabled={!canEdit} />
            </div>
          </FormSection>
        </Card>

        <Card>
          <FormSection legend={ar ? "شاشة الدخول" : "Login screen"}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label={ar ? "العنوان (عربي)" : "Headline (Arabic)"} value={form.loginHeadlineAr} onChange={(e) => set("loginHeadlineAr", e.target.value)} disabled={!canEdit} />
              <Input label={ar ? "العنوان (إنجليزي)" : "Headline (English)"} value={form.loginHeadlineEn} onChange={(e) => set("loginHeadlineEn", e.target.value)} disabled={!canEdit} />
            </div>
          </FormSection>
        </Card>

        <Card>
          <FormSection legend={ar ? "التذييل" : "Footer"}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label={ar ? "نص التذييل (عربي)" : "Footer text (Arabic)"} value={form.footerTextAr} onChange={(e) => set("footerTextAr", e.target.value)} disabled={!canEdit} />
              <Input label={ar ? "نص التذييل (إنجليزي)" : "Footer text (English)"} value={form.footerTextEn} onChange={(e) => set("footerTextEn", e.target.value)} disabled={!canEdit} />
            </div>
          </FormSection>
        </Card>

        {saveError && <ErrorText>{saveError}</ErrorText>}

        {canEdit && (
          <div className="flex justify-end">
            <Button type="submit" loading={saving}>{ar ? "حفظ" : "Save"}</Button>
          </div>
        )}
      </form>
    </div>
  );
}
